import moment from 'moment/moment';
import { Set } from 'immutable';
import { PAYMENT_STATUSES, PAYMENT_TAGS } from '@/domain/payments/constants';
import type { PaymentInfoRecord } from '@/domain/payments/types';
import { hasPaymentBeneficiary } from '@/domain/payments/helpers';

export const isWithholdingTaxFilled = (paymentInfo: PaymentInfoRecord): boolean =>
  paymentInfo.withholdingTax != null;

const hasRequiredPaymentFields = (paymentInfo: PaymentInfoRecord): boolean =>
  Boolean(paymentInfo.payFrom) &&
  hasPaymentBeneficiary(paymentInfo) &&
  // mandatory: it is sent to the bank as the payment description
  Boolean(paymentInfo.transferReason?.trim()) &&
  isWithholdingTaxFilled(paymentInfo) &&
  // isSameOrAfter (not !isBefore) so an empty/invalid pay date resolves to false and blocks submit.
  moment(paymentInfo.paymentDate).isSameOrAfter(moment(), 'day') &&
  parseFloat(paymentInfo.totalAmount) > 0;

export const canStartPaymentApprovalFlow = (paymentInfo: PaymentInfoRecord): boolean =>
  paymentInfo.status === PAYMENT_STATUSES.PREPARATION && hasRequiredPaymentFields(paymentInfo);

/**
 * Direct pay. Status is intentionally NOT checked here — the footer only renders the Pay button for
 * statuses where paying is allowed (PREPARATION without approval, APPROVED), so this predicate just
 * gates the required fields.
 */
export const canPayPayment = (paymentInfo: PaymentInfoRecord): boolean =>
  hasRequiredPaymentFields(paymentInfo);

export const canStartPaymentProcess = (paymentTags: Set<string>): boolean =>
  paymentTags.has(PAYMENT_TAGS.saveForLater);

export const canArchivePayment = (status: string): boolean =>
  ([
    PAYMENT_STATUSES.PENDING_APPROVAL,
    PAYMENT_STATUSES.APPROVED,
    PAYMENT_STATUSES.APPROVAL_REJECTED,
    PAYMENT_STATUSES.PROCESSING,
    PAYMENT_STATUSES.PROCESSING_VOID,
  ] as string[]).includes(status);
